import { useState, useMemo, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  ChevronLeft, ChevronRight, Plus, Trash2, Download,
  AlertTriangle, TrendingUp, DollarSign, Users, Calendar as CalendarIcon,
  Loader2, Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";
import { useAppContext } from "@/lib/onboardingContext";
import { useLocation } from "@/lib/locationContext";
import { getCurrencySymbol, convertFromUSD } from "@/lib/currency";
import { toast } from "sonner";
import {
  DAYS, type DayKey, SHIFTS, type Shift, SHIFT_HOURS,
  type Scenario,
  getRolesForBusinessType, recommendedCount,
  defaultDailyCovers, defaultDailyRevenue,
  venueKeyFromBusinessType,
  type RoleDef,
} from "@/lib/staffForecastData";

interface CustomRole {
  id: string;
  title: string;
  hourly_wage: number;
  default_shift: Shift;
}

// Per-role wage overrides keyed by role id (built-in or custom).
type WageMap = Record<string, number>;
// Per-role / per-day / per-shift override of recommended count. Negative or
// undefined means "use recommendation".
type StaffOverrideMap = Record<string, Record<DayKey, Record<Shift, number | undefined>>>;
// Actual labor cost recorded per day (manual entry, optional).
type ActualMap = Record<DayKey, number>;

interface ForecastData {
  covers: Record<DayKey, number>;
  revenue: Record<DayKey, number>;
  wages: WageMap;
  overrides: StaffOverrideMap;
  actuals: ActualMap;
}

function getMonday(d: Date): Date {
  const r = new Date(d);
  const day = r.getDay(); // 0=Sun..6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  r.setDate(r.getDate() + diff);
  r.setHours(0, 0, 0, 0);
  return r;
}

function dateStr(d: Date): string {
  return d.toISOString().split("T")[0];
}

function emptyOverrides(): StaffOverrideMap {
  return {};
}

function emptyActuals(): ActualMap {
  return DAYS.reduce((acc, d) => { acc[d] = 0; return acc; }, {} as ActualMap);
}

export default function WeeklyStaffForecast() {
  const { user } = useAuth();
  const { onboarding } = useAppContext();
  const { activeLocation } = useLocation();

  // Effective business type — prefer the active location's, fall back to onboarding.
  const businessType = activeLocation?.business_type || onboarding.businessType || "restaurant";
  const city = (activeLocation?.city || onboarding.city || "").toLowerCase();
  const cs = getCurrencySymbol(city);

  const builtInRoles = useMemo<RoleDef[]>(
    () => getRolesForBusinessType(businessType),
    [businessType],
  );

  const [customRoles, setCustomRoles] = useState<CustomRole[]>([]);
  const [scenario, setScenario] = useState<Scenario>("normal");
  const [weekStart, setWeekStart] = useState<Date>(getMonday(new Date()));
  const [data, setData] = useState<ForecastData>(() => ({
    covers: defaultDailyCovers(businessType),
    revenue: defaultDailyRevenue(businessType),
    wages: {},
    overrides: emptyOverrides(),
    actuals: emptyActuals(),
  }));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addRoleOpen, setAddRoleOpen] = useState(false);
  const [newRole, setNewRole] = useState<{ title: string; hourly_wage: number; default_shift: Shift }>({
    title: "", hourly_wage: 18, default_shift: "evening",
  });

  // Combine built-in + custom roles into a unified working list.
  const allRoles = useMemo(() => {
    const builtIn = builtInRoles.map(r => ({
      id: r.id,
      title: r.title,
      shifts: r.shifts,
      baseCount: r.baseCount,
      hourlyWageUSD: r.hourlyWageUSD,
      isCustom: false as const,
    }));
    const custom = customRoles.map(r => ({
      id: `custom-${r.id}`,
      title: r.title,
      shifts: [r.default_shift] as Shift[],
      baseCount: 1,
      hourlyWageUSD: r.hourly_wage, // already local currency for custom
      isCustom: true as const,
    }));
    return [...builtIn, ...custom];
  }, [builtInRoles, customRoles]);

  // Effective wage in local currency for a role (override > role default).
  const wageFor = useCallback((roleId: string): number => {
    const override = data.wages[roleId];
    if (typeof override === "number" && override > 0) return override;
    const builtIn = builtInRoles.find(r => r.id === roleId);
    if (builtIn) return convertFromUSD(builtIn.hourlyWageUSD, city);
    const custom = customRoles.find(r => `custom-${r.id}` === roleId);
    if (custom) return custom.hourly_wage;
    return 0;
  }, [data.wages, builtInRoles, customRoles, city]);

  // Demand index for a day — actual covers vs default baseline covers.
  const demandIndex = useCallback((day: DayKey): number => {
    const baseline = defaultDailyCovers(businessType)[day];
    const actual = data.covers[day] ?? baseline;
    return baseline > 0 ? actual / baseline : 1;
  }, [data.covers, businessType]);

  // Resolve recommended (or overridden) count for role × shift × day.
  const countFor = useCallback((role: typeof allRoles[number], day: DayKey, shift: Shift): number => {
    const override = data.overrides[role.id]?.[day]?.[shift];
    if (typeof override === "number" && override >= 0) return override;
    if (role.isCustom) {
      // Custom roles: 1 person on their default shift, scaled by scenario.
      if (shift !== role.shifts[0]) return 0;
      // Reuse the same heuristic engine for built-in calc.
      return recommendedCount(
        { id: role.id, title: role.title, shifts: role.shifts, baseCount: role.baseCount, hourlyWageUSD: 0 },
        shift, day, scenario, demandIndex(day),
      );
    }
    return recommendedCount(
      { id: role.id, title: role.title, shifts: role.shifts, baseCount: role.baseCount, hourlyWageUSD: role.hourlyWageUSD },
      shift, day, scenario, demandIndex(day),
    );
  }, [data.overrides, scenario, demandIndex]);

  // ─── Load custom roles + saved forecast ──────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user) { setLoading(false); return; }
      setLoading(true);
      // Custom roles for this user (optionally scoped to active location).
      const rolesQuery = supabase
        .from("custom_staff_roles")
        .select("*")
        .eq("user_id", user.id);
      if (activeLocation?.id) rolesQuery.eq("location_id", activeLocation.id);
      const { data: rolesRows } = await rolesQuery;

      // Forecast for this week + active location.
      const weekKey = dateStr(weekStart);
      let forecastQuery = supabase
        .from("weekly_staff_forecasts")
        .select("*")
        .eq("user_id", user.id)
        .eq("week_start", weekKey);
      if (activeLocation?.id) {
        forecastQuery = forecastQuery.eq("location_id", activeLocation.id);
      } else {
        forecastQuery = forecastQuery.is("location_id", null);
      }
      const { data: fc } = await forecastQuery.maybeSingle();

      if (cancelled) return;

      setCustomRoles(((rolesRows ?? []) as Array<{
        id: string; title: string; hourly_wage: number; default_shift: string;
      }>).map(r => ({
        id: r.id,
        title: r.title,
        hourly_wage: Number(r.hourly_wage),
        default_shift: (r.default_shift as Shift) ?? "morning",
      })));

      if (fc) {
        const stored = fc.data as Partial<ForecastData> | null;
        setData({
          covers: { ...defaultDailyCovers(businessType), ...stored?.covers },
          revenue: { ...defaultDailyRevenue(businessType), ...stored?.revenue },
          wages: stored?.wages ?? {},
          overrides: stored?.overrides ?? emptyOverrides(),
          actuals: { ...emptyActuals(), ...stored?.actuals },
        });
        setScenario((fc.scenario as Scenario) ?? "normal");
      } else {
        setData({
          covers: defaultDailyCovers(businessType),
          revenue: defaultDailyRevenue(businessType),
          wages: {},
          overrides: emptyOverrides(),
          actuals: emptyActuals(),
        });
        setScenario("normal");
      }
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [user, activeLocation?.id, weekStart, businessType]);

  // ─── Derived totals ──────────────────────────────────────────────────
  const dailyLaborCost = useMemo(() => {
    const out = {} as Record<DayKey, number>;
    for (const day of DAYS) {
      let total = 0;
      for (const role of allRoles) {
        const wage = wageFor(role.id);
        for (const sh of SHIFTS) {
          const count = countFor(role, day, sh.id);
          total += count * wage * SHIFT_HOURS[sh.id];
        }
      }
      out[day] = total;
    }
    return out;
  }, [allRoles, wageFor, countFor]);

  const weeklyLaborCost = useMemo(
    () => DAYS.reduce((s, d) => s + dailyLaborCost[d], 0),
    [dailyLaborCost],
  );

  const weeklyRevenueLocal = useMemo(
    () => DAYS.reduce((s, d) => s + convertFromUSD(data.revenue[d] ?? 0, city), 0),
    [data.revenue, city],
  );

  const laborPctOfRevenue = weeklyRevenueLocal > 0
    ? (weeklyLaborCost / weeklyRevenueLocal) * 100
    : 0;

  const weeklyActuals = useMemo(
    () => DAYS.reduce((s, d) => s + (data.actuals[d] ?? 0), 0),
    [data.actuals],
  );

  // ─── Mutations ───────────────────────────────────────────────────────
  const updateCovers = (day: DayKey, val: number) =>
    setData(d => ({ ...d, covers: { ...d.covers, [day]: val } }));

  const updateRevenue = (day: DayKey, val: number) =>
    setData(d => ({ ...d, revenue: { ...d.revenue, [day]: val } }));

  const updateWage = (roleId: string, val: number) =>
    setData(d => ({ ...d, wages: { ...d.wages, [roleId]: val } }));

  const updateOverride = (roleId: string, day: DayKey, shift: Shift, val: number | undefined) =>
    setData(d => {
      const nextRole = { ...d.overrides[roleId] } as Record<DayKey, Record<Shift, number | undefined>>;
      const nextDay = { ...nextRole[day], [shift]: val } as Record<Shift, number | undefined>;
      nextRole[day] = nextDay;
      return { ...d, overrides: { ...d.overrides, [roleId]: nextRole } };
    });

  const updateActual = (day: DayKey, val: number) =>
    setData(d => ({ ...d, actuals: { ...d.actuals, [day]: val } }));

  const addCustomRole = async () => {
    if (!user) { toast.error("You must be signed in to add custom roles."); return; }
    const title = newRole.title.trim();
    if (title.length < 2) { toast.error("Role title is too short."); return; }
    if (title.length > 60) { toast.error("Role title is too long (60 chars max)."); return; }
    if (newRole.hourly_wage < 0 || newRole.hourly_wage > 1000) { toast.error("Wage out of range."); return; }
    const { data: row, error } = await supabase
      .from("custom_staff_roles")
      .insert({
        user_id: user.id,
        location_id: activeLocation?.id ?? null,
        business_type: businessType,
        title,
        hourly_wage: newRole.hourly_wage,
        default_shift: newRole.default_shift,
      })
      .select()
      .single();
    if (error || !row) { toast.error("Could not save custom role."); return; }
    setCustomRoles(prev => [...prev, {
      id: row.id, title: row.title, hourly_wage: Number(row.hourly_wage), default_shift: row.default_shift as Shift,
    }]);
    setAddRoleOpen(false);
    setNewRole({ title: "", hourly_wage: 18, default_shift: "evening" });
    toast.success("Custom role added.");
  };

  const removeCustomRole = async (id: string) => {
    const { error } = await supabase.from("custom_staff_roles").delete().eq("id", id);
    if (error) { toast.error("Could not remove role."); return; }
    setCustomRoles(prev => prev.filter(r => r.id !== id));
  };

  const saveForecast = async () => {
    if (!user) { toast.error("You must be signed in to save forecasts."); return; }
    setSaving(true);
    const weekKey = dateStr(weekStart);
    const payload = {
      user_id: user.id,
      location_id: activeLocation?.id ?? null,
      week_start: weekKey,
      business_type: businessType,
      scenario,
      data: data as unknown as never,
    };
    const { error } = await supabase
      .from("weekly_staff_forecasts")
      .upsert([payload], { onConflict: "user_id,location_id,week_start" });
    setSaving(false);
    if (error) { toast.error("Could not save forecast."); return; }
    toast.success("Forecast saved.");
  };

  const prevWeek = () => {
    const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d);
  };
  const nextWeek = () => {
    const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d);
  };

  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  }), [weekStart]);

  const weekLabel = `${weekDates[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${weekDates[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

  // ─── PDF export ──────────────────────────────────────────────────────
  const exportPDF = () => {
    // Print-optimised: render an off-screen window with the forecast, then print to PDF.
    const w = window.open("", "_blank", "width=1200,height=800");
    if (!w) { toast.error("Pop-ups blocked — allow pop-ups to export."); return; }
    const fmt = (n: number) => `${cs}${Math.round(n).toLocaleString()}`;
    const rows = allRoles.map(role => {
      const tds = DAYS.map(day => {
        const total = SHIFTS.reduce((s, sh) => s + countFor(role, day, sh.id), 0);
        return `<td>${total}</td>`;
      }).join("");
      return `<tr><td><b>${role.title}</b><br/><small>${fmt(wageFor(role.id))}/hr</small></td>${tds}</tr>`;
    }).join("");
    const dailyRow = DAYS.map(d => `<td>${fmt(dailyLaborCost[d])}</td>`).join("");
    const coversRow = DAYS.map(d => `<td>${data.covers[d]}</td>`).join("");
    const revRow = DAYS.map(d => `<td>${fmt(convertFromUSD(data.revenue[d], city))}</td>`).join("");
    const html = `
      <html><head><title>Weekly Staff Forecast – ${weekLabel}</title>
      <style>
        @page { size: A4 landscape; margin: 12mm; }
        body { font-family: -apple-system, BlinkMacSystemFont, sans-serif; color: #111; font-size: 11px; }
        h1 { font-size: 18px; margin: 0 0 4px; }
        .meta { color: #666; font-size: 11px; margin-bottom: 12px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: center; }
        th { background: #f5f5f5; font-weight: 600; }
        td:first-child, th:first-child { text-align: left; }
        .totals td { background: #fafafa; font-weight: 600; }
        .warn { color: #b91c1c; font-weight: 600; }
        @media print { button { display: none; } }
      </style></head>
      <body>
        <h1>Weekly Staff Forecast</h1>
        <div class="meta">
          ${activeLocation?.name ?? onboarding.businessName ?? "Venue"} ·
          ${weekLabel} · Scenario: ${scenario} ·
          Total labor: <b>${fmt(weeklyLaborCost)}</b>
          (<span class="${laborPctOfRevenue > 32 ? "warn" : ""}">${laborPctOfRevenue.toFixed(1)}% of revenue</span>)
        </div>
        <table>
          <thead><tr><th>Role</th>${DAYS.map(d => `<th>${d}</th>`).join("")}</tr></thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr class="totals"><td>Expected covers</td>${coversRow}</tr>
            <tr class="totals"><td>Projected revenue</td>${revRow}</tr>
            <tr class="totals"><td>Daily labor cost</td>${dailyRow}</tr>
          </tfoot>
        </table>
        <button onclick="window.print()" style="padding: 8px 16px; font-size: 14px;">Print / Save as PDF</button>
      </body></html>
    `;
    w.document.write(html);
    w.document.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const venueLabel = venueKeyFromBusinessType(businessType);

  return (
    <div className="space-y-6">
      {/* Header strip */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={prevWeek}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/40">
            <CalendarIcon className="w-4 h-4 text-muted-foreground" />
            <span className="font-heading font-medium text-foreground text-sm">{weekLabel}</span>
          </div>
          <Button variant="outline" size="icon" onClick={nextWeek}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(getMonday(new Date()))}>
            This week
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <ToggleGroup
            type="single"
            value={scenario}
            onValueChange={(v) => v && setScenario(v as Scenario)}
            className="bg-muted/40 rounded-md"
          >
            <ToggleGroupItem value="quiet" className="text-xs px-3">Quiet</ToggleGroupItem>
            <ToggleGroupItem value="normal" className="text-xs px-3">Normal</ToggleGroupItem>
            <ToggleGroupItem value="busy" className="text-xs px-3">Busy</ToggleGroupItem>
          </ToggleGroup>
          <Button variant="outline" size="sm" onClick={exportPDF}>
            <Download className="w-4 h-4 mr-1.5" /> Export PDF
          </Button>
          <Button size="sm" onClick={saveForecast} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Save
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard
          icon={<Users className="w-3.5 h-3.5" />}
          label="Roles"
          value={String(allRoles.length)}
          hint={`${venueLabel.replace("-", " ")} venue`}
        />
        <SummaryCard
          icon={<DollarSign className="w-3.5 h-3.5" />}
          label="Weekly labor cost"
          value={`${cs}${Math.round(weeklyLaborCost).toLocaleString()}`}
        />
        <SummaryCard
          icon={<TrendingUp className="w-3.5 h-3.5" />}
          label="Projected revenue"
          value={`${cs}${Math.round(weeklyRevenueLocal).toLocaleString()}`}
        />
        <SummaryCard
          icon={laborPctOfRevenue > 32 ? <AlertTriangle className="w-3.5 h-3.5" /> : <DollarSign className="w-3.5 h-3.5" />}
          label="Labor % of revenue"
          value={`${laborPctOfRevenue.toFixed(1)}%`}
          hint={laborPctOfRevenue > 32 ? "Above healthy range (32%)" : "Within healthy range"}
          warn={laborPctOfRevenue > 32}
        />
      </div>

      {/* Inputs row: covers + revenue per day */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="font-heading font-medium text-sm text-foreground">Demand inputs</h3>
          <Badge variant="outline" className="text-[10px]">Editable</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground bg-muted/20">
                <th className="text-left px-3 py-2 font-medium w-44">Metric</th>
                {DAYS.map(d => (
                  <th key={d} className="px-2 py-2 font-medium">{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border">
                <td className="px-3 py-2 text-muted-foreground">Expected covers</td>
                {DAYS.map(d => (
                  <td key={d} className="px-1 py-1">
                    <Input
                      type="number"
                      min={0}
                      value={data.covers[d]}
                      onChange={e => updateCovers(d, Number(e.target.value) || 0)}
                      className="h-8 text-xs text-center"
                    />
                  </td>
                ))}
              </tr>
              <tr className="border-t border-border">
                <td className="px-3 py-2 text-muted-foreground">Projected revenue ({cs})</td>
                {DAYS.map(d => (
                  <td key={d} className="px-1 py-1">
                    <Input
                      type="number"
                      min={0}
                      value={Math.round(convertFromUSD(data.revenue[d], city))}
                      onChange={e => {
                        // Store back as USD so currency conversions stay consistent.
                        const local = Number(e.target.value) || 0;
                        const usd = convertFromUSD(1, city) > 0
                          ? local / (convertFromUSD(1, city))
                          : local;
                        updateRevenue(d, Math.round(usd));
                      }}
                      className="h-8 text-xs text-center"
                    />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Roles × shifts grid */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-heading font-medium text-sm text-foreground">Recommended staffing</h3>
            <p className="text-xs text-muted-foreground">
              Counts auto-adjust to scenario, day demand, and shift peaks. Override any cell.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setAddRoleOpen(true)}>
            <Plus className="w-4 h-4 mr-1" /> Add custom role
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground bg-muted/20">
                <th className="text-left px-3 py-2 font-medium w-48">Role</th>
                <th className="text-left px-2 py-2 font-medium w-24">Wage/hr</th>
                <th className="text-left px-2 py-2 font-medium w-24">Shift</th>
                {DAYS.map(d => (
                  <th key={d} className="px-2 py-2 font-medium">{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allRoles.map(role => (
                role.shifts.map((sh, shIdx) => (
                  <tr key={`${role.id}-${sh}`} className="border-t border-border">
                    {shIdx === 0 && (
                      <td rowSpan={role.shifts.length} className="px-3 py-2 align-top">
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <div className="font-medium text-foreground">{role.title}</div>
                            {role.isCustom && <Badge variant="secondary" className="text-[9px] mt-0.5">Custom</Badge>}
                          </div>
                          {role.isCustom && (
                            <button
                              onClick={() => removeCustomRole(role.id.replace(/^custom-/, ""))}
                              className="text-muted-foreground hover:text-destructive"
                              title="Remove custom role"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                    {shIdx === 0 && (
                      <td rowSpan={role.shifts.length} className="px-2 py-2 align-top">
                        <Input
                          type="number"
                          min={0}
                          step={0.5}
                          value={Math.round(wageFor(role.id) * 100) / 100}
                          onChange={e => updateWage(role.id, Number(e.target.value) || 0)}
                          className="h-8 text-xs"
                        />
                      </td>
                    )}
                    <td className="px-2 py-1">
                      <Badge variant="outline" className="text-[10px] capitalize">{sh}</Badge>
                    </td>
                    {DAYS.map(d => {
                      const rec = countFor(role, d, sh);
                      const override = data.overrides[role.id]?.[d]?.[sh];
                      const isOverridden = typeof override === "number" && override >= 0;
                      return (
                        <td key={d} className="px-1 py-1 text-center">
                          <Input
                            type="number"
                            min={0}
                            value={rec}
                            onChange={e => {
                              const v = Number(e.target.value);
                              updateOverride(role.id, d, sh, isNaN(v) ? undefined : v);
                            }}
                            className={`h-8 text-xs text-center ${isOverridden ? "border-primary/40" : ""}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Daily labor cost summary + Actual vs Forecast */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="font-heading font-medium text-sm text-foreground">Cost summary & actuals</h3>
          <p className="text-xs text-muted-foreground">
            Compare planned labor against what you actually spent each day.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground bg-muted/20">
                <th className="text-left px-3 py-2 font-medium w-44">Metric</th>
                {DAYS.map(d => <th key={d} className="px-2 py-2 font-medium">{d}</th>)}
                <th className="px-2 py-2 font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border">
                <td className="px-3 py-2 text-muted-foreground">Forecast labor</td>
                {DAYS.map(d => (
                  <td key={d} className="px-2 py-2 text-center text-foreground">
                    {cs}{Math.round(dailyLaborCost[d]).toLocaleString()}
                  </td>
                ))}
                <td className="px-2 py-2 text-center font-medium text-foreground">
                  {cs}{Math.round(weeklyLaborCost).toLocaleString()}
                </td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-3 py-2 text-muted-foreground">Actual labor ({cs})</td>
                {DAYS.map(d => (
                  <td key={d} className="px-1 py-1">
                    <Input
                      type="number"
                      min={0}
                      value={data.actuals[d] || 0}
                      onChange={e => updateActual(d, Number(e.target.value) || 0)}
                      className="h-8 text-xs text-center"
                    />
                  </td>
                ))}
                <td className="px-2 py-2 text-center font-medium text-foreground">
                  {cs}{Math.round(weeklyActuals).toLocaleString()}
                </td>
              </tr>
              <tr className="border-t border-border bg-muted/10">
                <td className="px-3 py-2 text-muted-foreground">Variance</td>
                {DAYS.map(d => {
                  const diff = (data.actuals[d] || 0) - dailyLaborCost[d];
                  return (
                    <td key={d} className={`px-2 py-2 text-center ${diff > 0 ? "text-destructive" : "text-emerald-600"}`}>
                      {diff > 0 ? "+" : ""}{cs}{Math.round(diff).toLocaleString()}
                    </td>
                  );
                })}
                <td className={`px-2 py-2 text-center font-medium ${weeklyActuals - weeklyLaborCost > 0 ? "text-destructive" : "text-emerald-600"}`}>
                  {weeklyActuals - weeklyLaborCost > 0 ? "+" : ""}
                  {cs}{Math.round(weeklyActuals - weeklyLaborCost).toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Add custom role dialog */}
      <Dialog open={addRoleOpen} onOpenChange={setAddRoleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add custom role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm">Role title</Label>
              <Input
                placeholder="e.g. Sommelier"
                value={newRole.title}
                onChange={e => setNewRole({ ...newRole, title: e.target.value })}
                maxLength={60}
                className="mt-1.5"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Hourly wage ({cs})</Label>
                <Input
                  type="number"
                  min={0}
                  max={1000}
                  step={0.5}
                  value={newRole.hourly_wage}
                  onChange={e => setNewRole({ ...newRole, hourly_wage: Number(e.target.value) || 0 })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-sm">Default shift</Label>
                <Select
                  value={newRole.default_shift}
                  onValueChange={(v) => setNewRole({ ...newRole, default_shift: v as Shift })}
                >
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SHIFTS.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.label} ({s.hours})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddRoleOpen(false)}>Cancel</Button>
            <Button onClick={addCustomRole}>Add role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({
  icon, label, value, hint, warn,
}: { icon: React.ReactNode; label: string; value: string; hint?: string; warn?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-card border rounded-xl p-4 ${warn ? "border-destructive/40" : "border-border"}`}
    >
      <div className={`flex items-center gap-2 text-xs mb-1 ${warn ? "text-destructive" : "text-muted-foreground"}`}>
        {icon} {label}
      </div>
      <p className={`text-2xl font-medium ${warn ? "text-destructive" : "text-foreground"}`}>{value}</p>
      {hint && <p className={`text-[11px] mt-0.5 ${warn ? "text-destructive/80" : "text-muted-foreground"}`}>{hint}</p>}
    </motion.div>
  );
}
